/* eslint-disable react-hooks/exhaustive-deps */
import ExpandMore from '@efex-components/common/ExpandMore';
import { CountriesList, FederalEntitiesList } from '@efex-services/auth/types';
import { UploadStaStakeHolders } from '@efex-services/client/types';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@efexteam/components';
import Autorenew from '@mui/icons-material/Autorenew';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Card,
  CardActions,
  CardContent,
  Collapse,
  Grid,
  Typography,
} from '@mui/material';
import useHandleStakeholderRequest from '@pages/onboarding/hooks/useHandleStakeholderRequest';
import LifeProof from '@pages/onboarding/USA/steps/StakeHolders/components/form/components/lifeProof';
import ObtainLinkStyles from '@pages/onboarding/USA/steps/StakeHolders/components/form/components/lifeProof/components/ObtainLink.styles';
import { aesDecryptCBC } from '@utils/crypt';
import { useContext, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StakeHolderId } from '../../constants/stakeHolderOptions';
import type {
  MexicoStakeHoldersFormData,
  StakeholderKYCStatus,
} from '../../types/stakeholders.type';
import AddressInfo from '../StakeHolders/components/AddressInfo';
import DocumentType from '../StakeHolders/components/DocumentType';
import PersonalInfo from '../StakeHolders/components/PersonalInfo';
import StakeHolderContext from '../StakeHolders/context/StakeHolderContext';

interface IStakeHolders {
  index: number;
  data?: MexicoStakeHoldersFormData & {
    businessSector?: {
      id: number;
    };
  };
  getStakeHolder: () => void;
  countries: CountriesList[];
  birthcountries: CountriesList[];
  stakeholderId?: number;
  remove: (index: number) => void;
  getCountries: (value: string) => void;
  getBirthCountries: (value: string) => void;
  getFederalEntities: (value: string) => void;
  federalEntities: FederalEntitiesList[];
}

const StakeHolderFormMX = ({
  index,
  data,
  countries,
  birthcountries,
  federalEntities,
  stakeholderId,
  remove,
  getCountries,
  getFederalEntities,
  getBirthCountries,
  getStakeHolder,
}: IStakeHolders) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<boolean>(!stakeholderId);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDeletion, setConfirmDeletion] = useState(false);

  const { id } = data || {};

  const {
    businessSectorId,
    deleteDocumensToDelete,
    getAllCurrentDocumentsIds,
    govermentIdDocuments,
  } = useContext(StakeHolderContext);

  const getName = (): string | undefined => {
    const { name, lastName, secondLastName } = data || {};
    if (!lastName) return name;
    return `${name} ${lastName} ${secondLastName ?? ''}`;
  };

  const methods = useForm<MexicoStakeHoldersFormData>({
    mode: 'all',
    defaultValues: {
      ...data,
      ssn: data?.ssn ? aesDecryptCBC(data?.ssn) : '',
      businessSectorId: data?.businessSector?.id,
    },
  });

  const { handleSubmit, setError, watch } = methods;

  const { executeRequest } = useHandleStakeholderRequest({
    country: 'MX',
    existingStakeholderId: id,
    setIsLoading,
    deleteDocumensToDelete,
    getAllCurrentDocumentsIds,
    govermentIdDocuments,
  });

  const entityTypeId = watch('entityTypeId');

  const handleStakeholderRequest = async (
    values: UploadStaStakeHolders | MexicoStakeHoldersFormData,
    documentIds?: number[]
  ) => {
    const sectorId = businessSectorId?.id || data?.businessSector?.id;

    if (!sectorId) {
      setError('businessSectorId', {
        message: t('defaultError') as string,
      });
      setIsLoading(false);
      return undefined;
    }

    const formValues = {
      ...values,
      businessSectorId: Number(sectorId),
    };

    return executeRequest(formValues, documentIds);
  };

  const onSubmit = handleSubmit(async (values) => {
    setIsLoading(true);

    await handleStakeholderRequest(values);

    getStakeHolder();

    setIsLoading(false);
  });

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleRemove = () => {
    if (id) {
      setConfirmDeletion(true);
      return;
    }
    remove(index);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} autoComplete="off" className="mt-3">
        <Card>
          <CardActions disableSpacing>
            <Typography variant="h6" className="capitalize">
              {getName() ?? t('onbStHlSectionTitle', { index: index + 1 })}
            </Typography>
            <ExpandMore
              expand={expanded}
              onClick={handleExpandClick}
              aria-expanded={expanded}
              aria-label="show more"
            >
              <ExpandMoreIcon />
            </ExpandMore>
          </CardActions>
          <Collapse in={expanded} timeout="auto">
            <CardContent>
              <Grid container rowSpacing={2} columnSpacing={2}>
                <PersonalInfo
                  data={data}
                  getCountries={getCountries}
                  birthcountries={birthcountries}
                  getBirthCountries={getBirthCountries}
                  federalEntities={federalEntities}
                  countries={countries}
                  getFederalEntities={getFederalEntities}
                />
                <DocumentType defaultValues={data} />
                <Grid item xs={12} />
                <AddressInfo data={data} />
                <Grid item xs={12} />

                {entityTypeId !== StakeHolderId.JURIDICAL_MX &&
                entityTypeId !== StakeHolderId.JURIDICAL_FR ? (
                  <LifeProof
                    kycStatus={data?.kycStatus as StakeholderKYCStatus}
                    handleStakeholderRequest={handleStakeholderRequest}
                    getStakeHolder={getStakeHolder}
                  />
                ) : null}
              </Grid>
              <CardActions className="flex justify-around gap-10">
                <Button
                  size="large"
                  type="button"
                  className="justify-around"
                  appearance="secondary"
                  disabled={isLoading}
                  onClick={handleRemove}
                  endIcon={!isLoading ? <DeleteIcon /> : undefined}
                  startIcon={
                    !isLoading ? undefined : (
                      <Autorenew className="animate-spin" />
                    )
                  }
                >
                  {t('onbRemove')}
                </Button>
                <Button
                  appearance="primary"
                  endIcon={
                    isLoading ? (
                      <Autorenew className="animate-spin" />
                    ) : undefined
                  }
                  disabled={isLoading}
                  size="large"
                  type="submit"
                >
                  {t(id ? 'update' : 'onbSaveStk')}
                </Button>
              </CardActions>
            </CardContent>
          </Collapse>
        </Card>
      </form>
      {confirmDeletion ? (
        <Dialog
          open={confirmDeletion}
          onClose={() => setConfirmDeletion(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle
            className={ObtainLinkStyles.title}
            showCloseButton
            onClick={() => setConfirmDeletion(false)}
          >
            <Typography variant="h6" className="!max-w-[80%] !mx-auto">
              {t('onbConfirmRemove')}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Button
              appearance="secondary"
              className="block mx-auto mt-5"
              endIcon={
                !isLoading ? (
                  <DeleteIcon />
                ) : (
                  <Autorenew className="animate-spin" />
                )
              }
              onClick={() => {
                remove(id ?? index);
                setConfirmDeletion(false);
              }}
            >
              {t('onbRemove')}
            </Button>
          </DialogContent>
        </Dialog>
      ) : null}
    </FormProvider>
  );
};

export default StakeHolderFormMX;
